import * as React from 'react';
import { History } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import axios from 'Src/helpers/api/axiosInstance';
import { getSuperAdminSessionKey } from 'Src/helpers/session/superadminSession';
import {
  createSuperAdminSport,
  getSuperAdminSport,
  SuperAdminSport,
  SuperAdminSportUpdateData,
  updateSuperAdminSport
} from 'Src/helpers/service/superadmin/sports';

const PLAYER_OPTIONS = [
  { value: 'TEAM', label: 'Team' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: '1X1', label: '1x1' },
  { value: '2X2', label: '2x2' }
];

const SCORING_OPTIONS = [
  { value: 'MORE_SCORES', label: 'More scores' },
  { value: 'LESS_SCORES', label: 'Less scores' },
  { value: 'MORE_TIME', label: 'More time' },
  { value: 'LESS_TIME', label: 'Less time' },
  { value: 'MORE_RESULT', label: 'More result' },
  { value: 'LESS_RESULT', label: 'Less result' },
  { value: 'FIRST_TO_N_POINTS', label: 'First to n points' }
];

const POINTS_DISPLAY_OPTIONS = [
  { value: 'PLAIN', label: 'Plain' },
  { value: 'TIME', label: 'Time' },
  { value: 'DISTANCE', label: 'Distance' },
  { value: 'PRESENCE_ONLY', label: 'Presence only' }
];

const POINTS_SIGN_OPTIONS = [
  { value: 'PLUS', label: 'Plus' },
  { value: 'MINUS', label: 'Minus' },
  { value: 'BOTH', label: 'Both' }
];

const FIELD_POSITION_SHAPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'CIRCLE', label: 'Circle' },
  { value: 'RECT', label: 'Rectangle' },
  { value: 'POLY', label: 'Polygon' }
];

interface Props {
  history: History;
  match: {
    params: {
      sportId?: string;
    };
  };
}

interface PerformanceDraftItem {
  name: string;
  minValue: string;
  maxValue: string;
}

interface DisciplineDraftItem {
  name: string;
  namePlural: string;
  description: string;
}

interface FieldPositionDraftItem {
  name: string;
  description: string;
  shape: string;
  coords: string;
}

interface AbbreviationDraftItem {
  code: string;
  description: string;
  color: string;
}

interface SportDraft {
  name: string;
  description: string;
  icon: string;
  tournamentFieldPicUrl: string;
  abbreviationAvailable: boolean;
  abbreviations: AbbreviationDraftItem[];
  players: string;
  scoring: string;
  multiparty: boolean;
  isRelay: boolean;
  relayLegsNumber: string;
  relayLegs: string;
  individualResultsAvailable: boolean;
  isSchoolEventsAvailable: boolean;
  isClubEventsAvailable: boolean;
  maleOnly: boolean;
  femaleOnly: boolean;
  mixed: boolean;
  minPlayers: string;
  maxPlayers: string;
  minSubs: string;
  maxSubs: string;
  format: string[];
  performance: PerformanceDraftItem[];
  discipline: DisciplineDraftItem[];
  fieldPic: string;
  fieldPositions: FieldPositionDraftItem[];
  pointsName: string;
  pointsNamePlural: string;
  pointsStep: string;
  pointsDisplay: string;
  pointsInputMask: string;
  pointsSign: string;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  isFieldPicLoading: boolean;
  isIconLoading: boolean;
  isTournamentFieldPicLoading: boolean;
  errorMessage: string;
  originalSport: SuperAdminSport | null;
  draft: SportDraft;
}

function getDefaultDraft(): SportDraft {
  return {
    name: '',
    description: '',
    icon: '',
    tournamentFieldPicUrl: '',
    abbreviationAvailable: false,
    abbreviations: [],
    players: 'TEAM',
    scoring: 'MORE_SCORES',
    multiparty: false,
    isRelay: false,
    relayLegsNumber: '',
    relayLegs: '',
    individualResultsAvailable: true,
    isSchoolEventsAvailable: true,
    isClubEventsAvailable: true,
    maleOnly: true,
    femaleOnly: true,
    mixed: true,
    minPlayers: '',
    maxPlayers: '',
    minSubs: '',
    maxSubs: '',
    format: [],
    performance: [],
    discipline: [],
    fieldPic: '',
    fieldPositions: [],
    pointsName: 'Point',
    pointsNamePlural: 'Points',
    pointsStep: '1',
    pointsDisplay: 'PLAIN',
    pointsInputMask: '',
    pointsSign: 'PLUS'
  };
}

function buildDraftFromSport(sport: SuperAdminSport): SportDraft {
  return {
    name: sport.name || '',
    description: sport.description || '',
    icon: sport.icon || '',
    tournamentFieldPicUrl: sport.tournamentFieldPicUrl || '',
    abbreviationAvailable: sport.abbreviationAvailable === true,
    abbreviations: Array.isArray(sport.abbreviations)
      ? sport.abbreviations
          .map(item => ({
            code: item && item.code ? item.code : '',
            description: item && item.description ? item.description : '',
            color: item && item.color ? item.color : '#000000'
          }))
          .filter(item => item.code.trim() !== '' || item.description.trim() !== '')
      : [],
    players: sport.players || 'TEAM',
    scoring: sport.scoring || 'MORE_SCORES',
    multiparty: sport.multiparty === true,
    isRelay: sport.isRelay === true,
    relayLegsNumber: sport.relay && typeof sport.relay.legsNumber === 'number' ? String(sport.relay.legsNumber) : '',
    relayLegs: sport.relay && Array.isArray(sport.relay.legs) ? sport.relay.legs.join(', ') : '',
    individualResultsAvailable: sport.individualResultsAvailable !== false,
    isSchoolEventsAvailable: sport.isSchoolEventsAvailable !== false,
    isClubEventsAvailable: sport.isClubEventsAvailable !== false,
    maleOnly: sport.genders && typeof sport.genders.maleOnly === 'boolean' ? sport.genders.maleOnly : true,
    femaleOnly: sport.genders && typeof sport.genders.femaleOnly === 'boolean' ? sport.genders.femaleOnly : true,
    mixed: sport.genders && typeof sport.genders.mixed === 'boolean' ? sport.genders.mixed : true,
    minPlayers:
      sport.defaultLimits && typeof sport.defaultLimits.minPlayers === 'number'
        ? String(sport.defaultLimits.minPlayers)
        : '',
    maxPlayers:
      sport.defaultLimits && typeof sport.defaultLimits.maxPlayers === 'number'
        ? String(sport.defaultLimits.maxPlayers)
        : '',
    minSubs:
      sport.defaultLimits && typeof sport.defaultLimits.minSubs === 'number' ? String(sport.defaultLimits.minSubs) : '',
    maxSubs:
      sport.defaultLimits && typeof sport.defaultLimits.maxSubs === 'number' ? String(sport.defaultLimits.maxSubs) : '',
    format: Array.isArray(sport.format)
      ? sport.format.map(item => (item && item.name ? item.name : '')).filter(name => name.trim() !== '')
      : [],
    performance: Array.isArray(sport.performance)
      ? sport.performance
          .map(item => ({
            name: item && item.name ? item.name : '',
            minValue: item && typeof item.minValue === 'number' ? String(item.minValue) : '',
            maxValue: item && typeof item.maxValue === 'number' ? String(item.maxValue) : ''
          }))
          .filter(item => item.name.trim() !== '')
      : [],
    discipline: Array.isArray(sport.discipline)
      ? sport.discipline
          .map(item => ({
            name: item && item.name ? item.name : '',
            namePlural: item && item.namePlural ? item.namePlural : '',
            description: item && item.description ? item.description : ''
          }))
          .filter(item => item.name.trim() !== '' || item.namePlural.trim() !== '' || item.description.trim() !== '')
      : [],
    fieldPic: sport.field && sport.field.pic ? sport.field.pic : '',
    fieldPositions:
      sport.field && Array.isArray(sport.field.positions)
        ? sport.field.positions
            .map(item => ({
              name: item && item.name ? item.name : '',
              description: item && item.description ? item.description : '',
              shape: item && item.shape ? item.shape : '',
              coords: item && Array.isArray(item.coords) ? item.coords.join(', ') : ''
            }))
            .filter(
              item =>
                item.name.trim() !== '' ||
                item.description.trim() !== '' ||
                item.shape.trim() !== '' ||
                item.coords.trim() !== ''
            )
        : [],
    pointsName: sport.points && sport.points.name ? sport.points.name : 'Point',
    pointsNamePlural: sport.points && sport.points.namePlural ? sport.points.namePlural : 'Points',
    pointsStep: sport.points && typeof sport.points.pointsStep === 'number' ? String(sport.points.pointsStep) : '1',
    pointsDisplay: sport.points && sport.points.display ? sport.points.display : 'PLAIN',
    pointsInputMask: sport.points && sport.points.inputMask ? sport.points.inputMask : '',
    pointsSign: sport.points && sport.points.sign ? sport.points.sign : 'PLUS'
  };
}

function parseOptionalNumber(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

function buildDefaultLimits(draft: SportDraft) {
  if (draft.players === '1X1') {
    return {
      minPlayers: 1,
      maxPlayers: 1,
      minSubs: parseOptionalNumber(draft.minSubs),
      maxSubs: parseOptionalNumber(draft.maxSubs)
    };
  }

  if (draft.players === '2X2') {
    return {
      minPlayers: 2,
      maxPlayers: 2,
      minSubs: parseOptionalNumber(draft.minSubs),
      maxSubs: parseOptionalNumber(draft.maxSubs)
    };
  }

  return {
    minPlayers: parseOptionalNumber(draft.minPlayers),
    maxPlayers: parseOptionalNumber(draft.maxPlayers),
    minSubs: parseOptionalNumber(draft.minSubs),
    maxSubs: parseOptionalNumber(draft.maxSubs)
  };
}

function buildFormat(format: string[]) {
  return format
    .map(name => name.trim())
    .filter(name => name !== '')
    .map(name => ({ name }));
}

function buildPerformance(performance: PerformanceDraftItem[]) {
  return performance
    .map(item => ({
      name: item.name.trim(),
      minValue: parseOptionalNumber(item.minValue),
      maxValue: parseOptionalNumber(item.maxValue)
    }))
    .filter(item => item.name !== '');
}

function buildDiscipline(discipline: DisciplineDraftItem[]) {
  return discipline
    .map(item => ({
      name: item.name.trim(),
      namePlural: item.namePlural.trim(),
      description: item.description.trim()
    }))
    .filter(item => item.name !== '' || item.namePlural !== '' || item.description !== '')
    .map(item => ({
      name: item.name,
      namePlural: item.namePlural,
      description: item.description
    }));
}

function buildCoords(coords: string): string[] {
  return coords
    .split(',')
    .map(coord => coord.trim())
    .filter(coord => coord !== '');
}

function buildField(draft: SportDraft, originalSport: SuperAdminSport | null) {
  const originalField = originalSport && originalSport.field ? originalSport.field : {};

  return {
    ...originalField,
    pic: draft.fieldPic.trim(),
    positions: draft.fieldPositions
      .map(item => ({
        name: item.name.trim(),
        description: item.description.trim(),
        shape: item.shape.trim(),
        coords: buildCoords(item.coords)
      }))
      .filter(item => item.name !== ''),
    teamPositions: originalField && Array.isArray(originalField.teamPositions) ? originalField.teamPositions : []
  };
}

function parseNumberList(value: string): number[] {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item !== '')
    .map(item => Number(item));
}

function buildRelay(draft: SportDraft) {
  if (!draft.isRelay) {
    return {};
  }

  return {
    legsNumber: parseOptionalNumber(draft.relayLegsNumber),
    legs: parseNumberList(draft.relayLegs)
  };
}

function buildAbbreviations(abbreviations: AbbreviationDraftItem[]) {
  return abbreviations
    .map(item => ({
      code: item.code.trim(),
      description: item.description.trim(),
      color: item.color.trim()
    }))
    .filter(item => item.code !== '');
}

function buildPayload(draft: SportDraft, originalSport: SuperAdminSport | null): SuperAdminSportUpdateData {
  return {
    ...(originalSport || {}),
    name: draft.name.trim(),
    description: draft.description.trim(),
    icon: draft.icon.trim(),
    tournamentFieldPicUrl: draft.tournamentFieldPicUrl.trim(),
    abbreviationAvailable: draft.abbreviationAvailable,
    abbreviations: buildAbbreviations(draft.abbreviations),
    players: draft.players,
    scoring: draft.scoring,
    multiparty: draft.multiparty,
    isRelay: draft.isRelay,
    individualResultsAvailable: draft.individualResultsAvailable,
    isSchoolEventsAvailable: draft.isSchoolEventsAvailable,
    isClubEventsAvailable: draft.isClubEventsAvailable,
    genders: {
      maleOnly: draft.maleOnly,
      femaleOnly: draft.femaleOnly,
      mixed: draft.mixed
    },
    field: buildField(draft, originalSport),
    points: {
      ...(originalSport && originalSport.points ? originalSport.points : {}),
      name: draft.pointsName.trim(),
      namePlural: draft.pointsNamePlural.trim(),
      pointsStep: Number(draft.pointsStep),
      display: draft.pointsDisplay,
      inputMask: draft.pointsInputMask.trim(),
      sign: draft.pointsSign
    },
    discipline: buildDiscipline(draft.discipline),
    performance: buildPerformance(draft.performance),
    defaultLimits: buildDefaultLimits(draft),
    relay: buildRelay(draft),
    format: buildFormat(draft.format)
  };
}

function uploadSuperAdminImage(file: File): Promise<string> {
  const sessionKey = getSuperAdminSessionKey();
  const formData = new FormData();

  formData.append('image', file);

  return axios
    .post(`${window.apiImg}/images`, formData, {
      headers: {
        asid: sessionKey,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      const key = response.data && response.data.key;

      if (!key) {
        throw new Error('Image upload did not return image key.');
      }

      return `${window.apiImg}/images/${key}`;
    });
}

export class SuperAdminSportForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      isSaving: false,
      isFieldPicLoading: false,
      isIconLoading: false,
      isTournamentFieldPicLoading: false,
      errorMessage: '',
      originalSport: null,
      draft: getDefaultDraft()
    };
  }

  componentDidMount() {
    const sportId = this.getSportId();

    if (sportId) {
      this.setState({ isLoading: true, errorMessage: '' });

      getSuperAdminSport(sportId)
        .then(sport => {
          this.setState({
            isLoading: false,
            originalSport: sport,
            draft: buildDraftFromSport(sport)
          });
        })
        .catch(error => {
          console.error('Can not load sport', error);
          this.setState({
            isLoading: false,
            errorMessage: 'Can not load sport. Please check the session and try again.'
          });
        });
    }
  }

  getSportId(): string | undefined {
    return this.props.match.params.sportId;
  }

  isEditMode(): boolean {
    return typeof this.getSportId() !== 'undefined';
  }

  onInputChange = (field: keyof SportDraft, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        [field]: value
      }
    }));
  };

  onCheckboxChange = (field: keyof SportDraft, event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        [field]: checked
      }
    }));
  };

  onFormatItemChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    this.setState(prevState => {
      const format = [...prevState.draft.format];
      format[index] = value;

      return {
        draft: {
          ...prevState.draft,
          format
        }
      };
    });
  };

  onAddFormatItemClick = () => {
    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        format: [...prevState.draft.format, '']
      }
    }));
  };

  onRemoveFormatItemClick = (index: number) => {
    this.setState(prevState => {
      const format = [...prevState.draft.format];
      format.splice(index, 1);

      return {
        draft: {
          ...prevState.draft,
          format
        }
      };
    });
  };

  onPerformanceItemChange = (
    index: number,
    field: keyof PerformanceDraftItem,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    this.setState(prevState => {
      const performance = [...prevState.draft.performance];
      performance[index] = {
        ...performance[index],
        [field]: value
      };

      return {
        draft: {
          ...prevState.draft,
          performance
        }
      };
    });
  };

  onAddPerformanceItemClick = () => {
    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        performance: [
          ...prevState.draft.performance,
          {
            name: '',
            minValue: '',
            maxValue: ''
          }
        ]
      }
    }));
  };

  onRemovePerformanceItemClick = (index: number) => {
    this.setState(prevState => {
      const performance = [...prevState.draft.performance];
      performance.splice(index, 1);

      return {
        draft: {
          ...prevState.draft,
          performance
        }
      };
    });
  };

  onDisciplineItemChange = (
    index: number,
    field: keyof DisciplineDraftItem,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    this.setState(prevState => {
      const discipline = [...prevState.draft.discipline];
      discipline[index] = {
        ...discipline[index],
        [field]: value
      };

      return {
        draft: {
          ...prevState.draft,
          discipline
        }
      };
    });
  };

  onAddDisciplineItemClick = () => {
    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        discipline: [
          ...prevState.draft.discipline,
          {
            name: '',
            namePlural: '',
            description: ''
          }
        ]
      }
    }));
  };

  onRemoveDisciplineItemClick = (index: number) => {
    this.setState(prevState => {
      const discipline = [...prevState.draft.discipline];
      discipline.splice(index, 1);

      return {
        draft: {
          ...prevState.draft,
          discipline
        }
      };
    });
  };

  onFieldPicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        fieldPic: value
      }
    }));
  };

  onFieldPicFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    this.setState({
      isFieldPicLoading: true,
      errorMessage: ''
    });

    uploadSuperAdminImage(file)
      .then(picUrl => {
        this.setState(prevState => ({
          isFieldPicLoading: false,
          draft: {
            ...prevState.draft,
            fieldPic: picUrl
          }
        }));
      })
      .catch(error => {
        console.error('Can not upload field picture', error);
        this.setState({
          isFieldPicLoading: false,
          errorMessage: 'Can not upload field picture. Please try again.'
        });
      });
  };

  onIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        icon: value
      }
    }));
  };

  onIconFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    this.setState({
      isIconLoading: true,
      errorMessage: ''
    });

    uploadSuperAdminImage(file)
      .then(iconUrl => {
        this.setState(prevState => ({
          isIconLoading: false,
          draft: {
            ...prevState.draft,
            icon: iconUrl
          }
        }));
      })
      .catch(error => {
        console.error('Can not upload sport icon', error);
        this.setState({
          isIconLoading: false,
          errorMessage: 'Can not upload sport icon. Please try again.'
        });
      });
  };

  onTournamentFieldPicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        tournamentFieldPicUrl: value
      }
    }));
  };

  onTournamentFieldPicFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    this.setState({
      isTournamentFieldPicLoading: true,
      errorMessage: ''
    });

    uploadSuperAdminImage(file)
      .then(picUrl => {
        this.setState(prevState => ({
          isTournamentFieldPicLoading: false,
          draft: {
            ...prevState.draft,
            tournamentFieldPicUrl: picUrl
          }
        }));
      })
      .catch(error => {
        console.error('Can not upload tournament website background picture', error);
        this.setState({
          isTournamentFieldPicLoading: false,
          errorMessage: 'Can not upload tournament website background picture. Please try again.'
        });
      });
  };

  onFieldPositionItemChange = (
    index: number,
    field: keyof FieldPositionDraftItem,
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = event.target.value;

    this.setState(prevState => {
      const fieldPositions = [...prevState.draft.fieldPositions];
      fieldPositions[index] = {
        ...fieldPositions[index],
        [field]: value
      };

      return {
        draft: {
          ...prevState.draft,
          fieldPositions
        }
      };
    });
  };

  onAddFieldPositionItemClick = () => {
    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        fieldPositions: [
          ...prevState.draft.fieldPositions,
          {
            name: '',
            description: '',
            shape: '',
            coords: ''
          }
        ]
      }
    }));
  };

  onRemoveFieldPositionItemClick = (index: number) => {
    this.setState(prevState => {
      const fieldPositions = [...prevState.draft.fieldPositions];
      fieldPositions.splice(index, 1);

      return {
        draft: {
          ...prevState.draft,
          fieldPositions
        }
      };
    });
  };

  onAbbreviationItemChange = (
    index: number,
    field: keyof AbbreviationDraftItem,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    this.setState(prevState => {
      const abbreviations = [...prevState.draft.abbreviations];
      abbreviations[index] = {
        ...abbreviations[index],
        [field]: value
      };

      return {
        draft: {
          ...prevState.draft,
          abbreviations
        }
      };
    });
  };

  onAddAbbreviationItemClick = () => {
    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        abbreviations: [
          ...prevState.draft.abbreviations,
          {
            code: '',
            description: '',
            color: '#000000'
          }
        ]
      }
    }));
  };

  onRemoveAbbreviationItemClick = (index: number) => {
    this.setState(prevState => {
      const abbreviations = [...prevState.draft.abbreviations];
      abbreviations.splice(index, 1);

      return {
        draft: {
          ...prevState.draft,
          abbreviations
        }
      };
    });
  };

  onBackClick = () => {
    this.props.history.push('/sports');
  };

  validate(): string {
    const { draft } = this.state;

    if (draft.name.trim() === '') {
      return 'Sport name is required.';
    }

    if (draft.pointsName.trim() === '') {
      return 'Points name is required.';
    }

    if (draft.pointsNamePlural.trim() === '') {
      return 'Points plural name is required.';
    }

    if (draft.pointsStep.trim() === '' || isNaN(Number(draft.pointsStep))) {
      return 'Points step should be a number.';
    }

    if (!draft.maleOnly && !draft.femaleOnly && !draft.mixed) {
      return 'At least one gender option should be selected.';
    }

    for (let index = 0; index < draft.abbreviations.length; index++) {
      const item = draft.abbreviations[index];
      const hasAnyValue = item.code.trim() !== '' || item.description.trim() !== '';

      if (hasAnyValue && item.code.trim() === '') {
        return `Abbreviation item ${index + 1} code is required.`;
      }
    }

    const numericFields = [
      { value: draft.minPlayers, label: 'Min players' },
      { value: draft.maxPlayers, label: 'Max players' },
      { value: draft.minSubs, label: 'Min subs' },
      { value: draft.maxSubs, label: 'Max subs' },
      ...draft.performance.map((item, index) => ({
        value: item.minValue,
        label: `Performance item ${index + 1} min value`
      })),
      ...draft.performance.map((item, index) => ({
        value: item.maxValue,
        label: `Performance item ${index + 1} max value`
      }))
    ];

    for (const field of numericFields) {
      if (field.value.trim() !== '' && isNaN(Number(field.value))) {
        return `${field.label} should be a number.`;
      }
    }

    if (draft.players !== '1X1' && draft.players !== '2X2') {
      const minPlayers = parseOptionalNumber(draft.minPlayers);
      const maxPlayers = parseOptionalNumber(draft.maxPlayers);

      if (typeof minPlayers === 'number' && typeof maxPlayers === 'number' && minPlayers > maxPlayers) {
        return 'Min players should not be greater than max players.';
      }
    }

    const minSubs = parseOptionalNumber(draft.minSubs);
    const maxSubs = parseOptionalNumber(draft.maxSubs);

    if (typeof minSubs === 'number' && typeof maxSubs === 'number' && minSubs > maxSubs) {
      return 'Min subs should not be greater than max subs.';
    }

    if (draft.isRelay) {
      const relayLegsNumber = parseOptionalNumber(draft.relayLegsNumber);
      const relayLegs = parseNumberList(draft.relayLegs);

      if (draft.relayLegsNumber.trim() === '' || isNaN(Number(draft.relayLegsNumber))) {
        return 'Relay legs number should be a number.';
      }

      if (typeof relayLegsNumber === 'number' && relayLegsNumber < 2) {
        return 'Relay legs number should be at least 2.';
      }

      if (draft.relayLegs.trim() !== '' && relayLegs.some(item => isNaN(item))) {
        return 'Relay legs should be a comma-separated list of numbers.';
      }

      if (typeof relayLegsNumber === 'number' && relayLegs.length > 0 && relayLegs.length !== relayLegsNumber) {
        return 'Relay legs count should match relay legs number.';
      }
    }

    for (let index = 0; index < draft.performance.length; index++) {
      const item = draft.performance[index];

      if (item.name.trim() === '' && (item.minValue.trim() !== '' || item.maxValue.trim() !== '')) {
        return `Performance item ${index + 1} name is required.`;
      }

      const minValue = parseOptionalNumber(item.minValue);
      const maxValue = parseOptionalNumber(item.maxValue);

      if (typeof minValue === 'number' && typeof maxValue === 'number' && minValue > maxValue) {
        return `Performance item ${index + 1} min value should not be greater than max value.`;
      }
    }

    for (let index = 0; index < draft.discipline.length; index++) {
      const item = draft.discipline[index];
      const hasAnyValue = item.name.trim() !== '' || item.namePlural.trim() !== '' || item.description.trim() !== '';

      if (hasAnyValue && item.name.trim() === '') {
        return `Discipline item ${index + 1} name is required.`;
      }

      if (hasAnyValue && item.namePlural.trim() === '') {
        return `Discipline item ${index + 1} plural name is required.`;
      }
    }

    for (let index = 0; index < draft.fieldPositions.length; index++) {
      const item = draft.fieldPositions[index];
      const hasAnyValue =
        item.name.trim() !== '' ||
        item.description.trim() !== '' ||
        item.shape.trim() !== '' ||
        item.coords.trim() !== '';

      if (hasAnyValue && item.name.trim() === '') {
        return `Field position ${index + 1} name is required.`;
      }

      if (item.shape.trim() === '' && item.coords.trim() !== '') {
        return `Field position ${index + 1} shape is required when coords are specified.`;
      }

      if (item.shape.trim() !== '' && item.coords.trim() === '') {
        return `Field position ${index + 1} coords are required when shape is specified.`;
      }
    }

    return '';
  }

  onSaveClick = () => {
    const validationError = this.validate();

    if (validationError) {
      this.setState({ errorMessage: validationError });
      return;
    }

    const { originalSport, draft } = this.state;
    const sportId = this.getSportId();
    const payload = buildPayload(draft, originalSport);

    this.setState({ isSaving: true, errorMessage: '' });

    const savePromise =
      this.isEditMode() && sportId ? updateSuperAdminSport(sportId, payload) : createSuperAdminSport(payload);

    savePromise
      .then(() => {
        this.props.history.push('/sports');
      })
      .catch(error => {
        console.error('Can not save sport', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not save sport. Please check the required fields and try again.'
        });
      });
  };

  renderSelect(field: keyof SportDraft, label: string, options: Array<{ value: string; label: string }>) {
    const { draft } = this.state;

    return (
      <div className="form-group">
        <label>{label}</label>
        <select
          className="form-control"
          value={draft[field] as string}
          onChange={event => this.onInputChange(field, event)}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderDisciplineItems() {
    const { draft } = this.state;

    return (
      <div>
        {draft.discipline.length === 0 && (
          <div className="text-muted mb-2">No discipline items have been added yet.</div>
        )}

        {draft.discipline.map((item, index) => (
          <div className="row align-items-end mb-2" key={`discipline-${index}`}>
            <div className="col-md-3">
              <div className="form-group mb-0">
                <label>Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.name}
                  onChange={event => this.onDisciplineItemChange(index, 'name', event)}
                  placeholder="Yellow card"
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group mb-0">
                <label>Plural name</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.namePlural}
                  onChange={event => this.onDisciplineItemChange(index, 'namePlural', event)}
                  placeholder="Yellow cards"
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group mb-0">
                <label>Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.description}
                  onChange={event => this.onDisciplineItemChange(index, 'description', event)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => this.onRemoveDisciplineItemClick(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="btn btn-outline-primary" onClick={this.onAddDisciplineItemClick}>
          Add discipline item
        </button>
      </div>
    );
  }

  renderAbbreviations() {
    const { draft } = this.state;

    return (
      <div>
        <div className="mb-2">{this.renderCheckbox('abbreviationAvailable', 'Abbreviations available')}</div>

        {draft.abbreviationAvailable && (
          <div>
            {draft.abbreviations.length === 0 && (
              <div className="text-muted mb-2">No abbreviations have been added yet.</div>
            )}

            {draft.abbreviations.map((item, index) => (
              <div className="row align-items-end mb-2" key={`abbreviation-${index}`}>
                <div className="col-md-2">
                  <div className="form-group mb-0">
                    <label>Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item.code}
                      onChange={event => this.onAbbreviationItemChange(index, 'code', event)}
                      placeholder="DNF"
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group mb-0">
                    <label>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item.description}
                      onChange={event => this.onAbbreviationItemChange(index, 'description', event)}
                      placeholder="Did not finish"
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group mb-0">
                    <label>Color</label>
                    <input
                      type="color"
                      className="form-control"
                      value={item.color || '#000000'}
                      onChange={event => this.onAbbreviationItemChange(index, 'color', event)}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => this.onRemoveAbbreviationItemClick(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <button type="button" className="btn btn-outline-primary" onClick={this.onAddAbbreviationItemClick}>
              Add abbreviation
            </button>
          </div>
        )}
      </div>
    );
  }

  renderSportImages() {
    const { draft, isIconLoading, isTournamentFieldPicLoading } = this.state;

    return (
      <div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>Sport icon</label>

              {draft.icon && (
                <div className="mb-2">
                  <img
                    src={draft.icon}
                    alt="Sport icon"
                    style={{
                      maxWidth: 90,
                      maxHeight: 90,
                      border: '1px solid #ddd',
                      borderRadius: 4
                    }}
                  />
                </div>
              )}

              <input
                type="file"
                className="form-control-file"
                accept="image/*"
                onChange={this.onIconFileChange}
                disabled={isIconLoading}
              />

              {isIconLoading && <div className="text-muted mt-1">Uploading...</div>}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Sport icon URL</label>
              <input
                type="text"
                className="form-control"
                value={draft.icon}
                onChange={this.onIconChange}
                placeholder="Optional sport icon URL"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>Tournament website background picture</label>

              {draft.tournamentFieldPicUrl && (
                <div className="mb-2">
                  <img
                    src={draft.tournamentFieldPicUrl}
                    alt="Tournament website background"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 160,
                      border: '1px solid #ddd',
                      borderRadius: 4
                    }}
                  />
                </div>
              )}

              <input
                type="file"
                className="form-control-file"
                accept="image/*"
                onChange={this.onTournamentFieldPicFileChange}
                disabled={isTournamentFieldPicLoading}
              />

              {isTournamentFieldPicLoading && <div className="text-muted mt-1">Uploading...</div>}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Tournament website background picture URL</label>
              <input
                type="text"
                className="form-control"
                value={draft.tournamentFieldPicUrl}
                onChange={this.onTournamentFieldPicChange}
                placeholder="Optional tournament background URL"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderRelaySettings() {
    const { draft } = this.state;

    if (!draft.isRelay) {
      return null;
    }

    return (
      <div>
        <div className="alert alert-info py-2">Relay settings are only used when Relay sport is enabled.</div>

        <div className="row">
          <div className="col-md-2">
            <div className="form-group">
              <label>Relay legs number</label>
              <input
                type="text"
                className="form-control"
                value={draft.relayLegsNumber}
                onChange={event => this.onInputChange('relayLegsNumber', event)}
                placeholder="4"
              />
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label>Relay legs</label>
              <input
                type="text"
                className="form-control"
                value={draft.relayLegs}
                onChange={event => this.onInputChange('relayLegs', event)}
                placeholder="100, 100, 200, 400"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFieldPositions() {
    const { draft } = this.state;

    return (
      <div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>Field picture</label>

              {draft.fieldPic && (
                <div className="mb-2">
                  <img
                    src={draft.fieldPic}
                    alt="Field"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 180,
                      border: '1px solid #ddd',
                      borderRadius: 4
                    }}
                  />
                </div>
              )}

              <input
                type="file"
                className="form-control-file"
                accept="image/*"
                onChange={this.onFieldPicFileChange}
                disabled={this.state.isFieldPicLoading}
              />

              {this.state.isFieldPicLoading && <div className="text-muted mt-1">Uploading...</div>}
            </div>
          </div>

          <div className="col-md-5">
            <div className="form-group">
              <label>Field picture URL</label>
              <input
                type="text"
                className="form-control"
                value={draft.fieldPic}
                onChange={this.onFieldPicChange}
                placeholder="Optional field picture URL"
              />
            </div>
          </div>
        </div>

        {draft.fieldPositions.length === 0 && (
          <div className="text-muted mb-2">No field positions have been added yet.</div>
        )}

        {draft.fieldPositions.map((item, index) => (
          <div className="row align-items-end mb-2" key={`field-position-${index}`}>
            <div className="col-md-2">
              <div className="form-group mb-0">
                <label>Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.name}
                  onChange={event => this.onFieldPositionItemChange(index, 'name', event)}
                  placeholder="Goalkeeper"
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group mb-0">
                <label>Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.description}
                  onChange={event => this.onFieldPositionItemChange(index, 'description', event)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group mb-0">
                <label>Shape</label>
                <select
                  className="form-control"
                  value={item.shape}
                  onChange={event => this.onFieldPositionItemChange(index, 'shape', event)}
                >
                  {FIELD_POSITION_SHAPE_OPTIONS.map(option => (
                    <option key={option.value || 'not-set'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group mb-0">
                <label>Coords</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.coords}
                  onChange={event => this.onFieldPositionItemChange(index, 'coords', event)}
                  placeholder="10, 20, 30"
                />
              </div>
            </div>

            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => this.onRemoveFieldPositionItemClick(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="btn btn-outline-primary" onClick={this.onAddFieldPositionItemClick}>
          Add field position
        </button>
      </div>
    );
  }

  renderPerformanceItems() {
    const { draft } = this.state;

    return (
      <div>
        {draft.performance.length === 0 && (
          <div className="text-muted mb-2">No performance items have been added yet.</div>
        )}

        {draft.performance.map((item, index) => (
          <div className="row align-items-end mb-2" key={`performance-${index}`}>
            <div className="col-md-4">
              <div className="form-group mb-0">
                <label>Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.name}
                  onChange={event => this.onPerformanceItemChange(index, 'name', event)}
                  placeholder="Enter performance name"
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group mb-0">
                <label>Min value</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.minValue}
                  onChange={event => this.onPerformanceItemChange(index, 'minValue', event)}
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group mb-0">
                <label>Max value</label>
                <input
                  type="text"
                  className="form-control"
                  value={item.maxValue}
                  onChange={event => this.onPerformanceItemChange(index, 'maxValue', event)}
                />
              </div>
            </div>

            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => this.onRemovePerformanceItemClick(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="btn btn-outline-primary" onClick={this.onAddPerformanceItemClick}>
          Add performance item
        </button>
      </div>
    );
  }

  renderFormatItems() {
    const { draft } = this.state;

    return (
      <div>
        {draft.format.length === 0 && <div className="text-muted mb-2">No format items have been added yet.</div>}

        {draft.format.map((formatName, index) => (
          <div className="row align-items-end mb-2" key={`format-${index}`}>
            <div className="col-md-4">
              <div className="form-group mb-0">
                <label>Item name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formatName}
                  onChange={event => this.onFormatItemChange(index, event)}
                  placeholder="Enter format name"
                />
              </div>
            </div>

            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => this.onRemoveFormatItemClick(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="btn btn-outline-primary" onClick={this.onAddFormatItemClick}>
          Add format item
        </button>
      </div>
    );
  }

  renderCheckbox(field: keyof SportDraft, label: string) {
    const { draft } = this.state;

    return (
      <div className="form-check mb-2">
        <input
          id={`sport-${field}`}
          type="checkbox"
          className="form-check-input"
          checked={draft[field] as boolean}
          onChange={event => this.onCheckboxChange(field, event)}
        />
        <label className="form-check-label" htmlFor={`sport-${field}`}>
          {label}
        </label>
      </div>
    );
  }

  render() {
    const { draft, errorMessage, isLoading, isSaving } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          {' '}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{this.isEditMode() ? 'Edit sport' : 'Add sport'}</h2>
              <div className="text-muted">Basic sport settings. Advanced sport configuration can be added later.</div>
            </div>
            <button className="btn btn-outline-secondary" onClick={this.onBackClick} disabled={isSaving}>
              Back to sports
            </button>
          </div>
          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.name}
                      onChange={event => this.onInputChange('name', event)}
                      placeholder="Enter sport name"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.description}
                      onChange={event => this.onInputChange('description', event)}
                      placeholder="Enter sport description"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">{this.renderSelect('players', 'Players', PLAYER_OPTIONS)}</div>
                <div className="col-md-3">{this.renderSelect('scoring', 'Scoring', SCORING_OPTIONS)}</div>
              </div>

              <hr />

              <h5>Images</h5>
              {this.renderSportImages()}

              <hr />

              <h5>Abbreviations</h5>
              {this.renderAbbreviations()}

              <hr />

              <h5>Availability</h5>

              <div className="row">
                <div className="col-md-3">
                  {this.renderCheckbox('isSchoolEventsAvailable', 'Available for school events')}
                </div>
                <div className="col-md-3">
                  {this.renderCheckbox('isClubEventsAvailable', 'Available for club events')}
                </div>
                <div className="col-md-3">
                  {this.renderCheckbox('individualResultsAvailable', 'Individual results available')}
                </div>
              </div>

              <hr />

              <h5>Event options</h5>
              <div className="row">
                <div className="col-md-3">{this.renderCheckbox('multiparty', 'Multiparty sport')}</div>
                <div className="col-md-3">{this.renderCheckbox('isRelay', 'Relay sport')}</div>
              </div>

              {this.renderRelaySettings()}

              <hr />

              <h5>Genders</h5>
              <div className="row">
                <div className="col-md-3">{this.renderCheckbox('maleOnly', 'Male only allowed')}</div>
                <div className="col-md-3">{this.renderCheckbox('femaleOnly', 'Female only allowed')}</div>
                <div className="col-md-3">{this.renderCheckbox('mixed', 'Mixed allowed')}</div>
              </div>

              <hr />

              <h5>Default limits</h5>
              {(draft.players === '1X1' || draft.players === '2X2') && (
                <div className="alert alert-info py-2">
                  Player limits are fixed automatically for {draft.players === '1X1' ? '1x1' : '2x2'} sports.
                </div>
              )}

              <div className="row">
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Min players</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.players === '1X1' ? '1' : draft.players === '2X2' ? '2' : draft.minPlayers}
                      onChange={event => this.onInputChange('minPlayers', event)}
                      disabled={draft.players === '1X1' || draft.players === '2X2'}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label>Max players</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.players === '1X1' ? '1' : draft.players === '2X2' ? '2' : draft.maxPlayers}
                      onChange={event => this.onInputChange('maxPlayers', event)}
                      disabled={draft.players === '1X1' || draft.players === '2X2'}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label>Min subs</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.minSubs}
                      onChange={event => this.onInputChange('minSubs', event)}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label>Max subs</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.maxSubs}
                      onChange={event => this.onInputChange('maxSubs', event)}
                    />
                  </div>
                </div>
              </div>

              <hr />

              <h5>Format</h5>
              {this.renderFormatItems()}

              <hr />

              <h5>Performance</h5>
              {this.renderPerformanceItems()}

              <hr />

              <h5>Discipline</h5>
              {this.renderDisciplineItems()}

              <hr />

              <h5>Field positions</h5>
              {this.renderFieldPositions()}

              <hr />

              <h5>Points</h5>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Points name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.pointsName}
                      onChange={event => this.onInputChange('pointsName', event)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Points plural name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.pointsNamePlural}
                      onChange={event => this.onInputChange('pointsNamePlural', event)}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Points step</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.pointsStep}
                      onChange={event => this.onInputChange('pointsStep', event)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  {this.renderSelect('pointsDisplay', 'Points display', POINTS_DISPLAY_OPTIONS)}
                </div>
                <div className="col-md-3">{this.renderSelect('pointsSign', 'Points sign', POINTS_SIGN_OPTIONS)}</div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Input mask</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.pointsInputMask}
                      onChange={event => this.onInputChange('pointsInputMask', event)}
                      placeholder="Optional, for example hh:mm:ss"
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button className="btn btn-outline-secondary mr-2" onClick={this.onBackClick} disabled={isSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={this.onSaveClick} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
